import type {
  DatePresetCustom,
  DatePresetKey,
  ResolvedDateRange,
} from "#app/lib/utils/date-presets";

/**
 * The equal-length window immediately before `range`, as a custom preset value
 * — what a windowed stat compares itself against. Day-aligned
 * in, day-aligned out: a resolved preset starts at a local 00:00:00.000 and
 * ends at a 23:59:59.999, so the previous end (start − 1ms) and start
 * (end − span) land on the same boundaries, and `resolveDatePreset("custom")`'s
 * startOfDay/endOfDay normalization is an identity over the result. Across a
 * DST shift the raw span is off by an hour and that same normalization snaps
 * it back to the day boundary.
 */
export function previousWindow(range: ResolvedDateRange): DatePresetCustom {
  const start = Date.parse(range.startDate);
  const end = Date.parse(range.endDate);
  const prevEnd = start - 1;
  const prevStart = prevEnd - (end - start);
  return {
    startDate: new Date(prevStart).toISOString(),
    endDate: new Date(prevEnd).toISOString(),
  };
}

/**
 * How the comparison window reads in copy — "vs 7 <basis>". Only the
 * calendar-length presets name their previous window exactly; a to-date or
 * custom window compares against an equal-length stretch with no calendar
 * name, so it reads as "the previous period". `all-time` has no previous
 * window at all — null, and the caller shows no comparison.
 */
const COMPARISON_BASIS: Record<DatePresetKey, string | null> = {
  "all-time": null,
  today: "yesterday",
  yesterday: "the day before",
  "this-week": "last week",
  "last-week": "the week before",
  mtd: "the previous period",
  "last-month": "the previous period",
  ytd: "the previous period",
  custom: "the previous period",
};

export function comparisonBasis(key: DatePresetKey): string | null {
  return COMPARISON_BASIS[key];
}
